import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth, type ZifekUser } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { resolveStoreName } from '@/lib/display';
import { fetchActiveCustomDomain } from '@/lib/customDomain';
import {
  TYPE_COMPTE_VENDEUR,
} from '@/lib/marketplace';
import VendorProductForm, { type AnnonceItem } from './components/VendorProductForm';
import VendorStatsTab from './components/VendorStatsTab';
import VendorBannersTab from './components/VendorBannersTab';
import VendorAppsTab from './components/VendorAppsTab';
import VendorOverview from './components/VendorOverview';
import VendorReservations from './components/VendorReservations';
import VendorCategoryCard from './components/VendorCategoryCard';
import VendorSettings from './components/VendorSettings';
import {
  fetchVendorRelation,
  setVendorFeaturedAnnonce,
  clearVendorFeaturedAnnonce,
  setAnnonceDisponible,
  isProductType,
  isServiceType,
  isClassifiedType,
  type VendorRelation,
} from '@/lib/marketplaceCatalog';
import { fetchVendorReservationCount, fetchVendorReservationStats, type VendorReservationStats } from '@/lib/vendorNotifications';

interface CommandeItem {
  id: number;
  titre: string | null;
  nomclient: string | null;
  quantite: number | null;
  totalttc: string | null;
  statut_com_vendeur: string | null;
  date_time: string | null;
  ville: string | null;
}

type Tab =
  | 'overview'
  | 'produits'
  | 'services'
  | 'annonces'
  | 'commandes'
  | 'reservations'
  | 'statistiques'
  | 'bannieres'
  | 'categorie'
  | 'applications'
  | 'parametres';

const NAV: { key: Tab; label: string; icon: string }[] = [
  { key: 'overview', label: "Vue d'ensemble", icon: 'ri-dashboard-line' },
  { key: 'produits', label: 'Mes produits', icon: 'ri-shopping-bag-3-line' },
  { key: 'services', label: 'Mes services', icon: 'ri-service-line' },
  { key: 'annonces', label: 'Mes annonces', icon: 'ri-megaphone-line' },
  { key: 'commandes', label: 'Commandes', icon: 'ri-shopping-cart-2-line' },
  { key: 'reservations', label: 'Réservations', icon: 'ri-calendar-check-line' },
  { key: 'statistiques', label: 'Statistiques', icon: 'ri-line-chart-line' },
  { key: 'bannieres', label: 'Bannières', icon: 'ri-image-2-line' },
  { key: 'categorie', label: 'Ma catégorie', icon: 'ri-price-tag-3-line' },
  { key: 'applications', label: 'Applications', icon: 'ri-apps-2-line' },
  { key: 'parametres', label: 'Paramètres', icon: 'ri-settings-3-line' },
];

const ORDER_STATUSES = ['En attente', 'Confirmée', 'Livrée'] as const;
type OrderStatus = (typeof ORDER_STATUSES)[number];

function orderStatusOf(c: CommandeItem): OrderStatus {
  const raw = (c.statut_com_vendeur || '').trim();
  if (raw === 'Confirmée' || raw === 'Livrée') return raw;
  return 'En attente';
}

function statusColor(status: OrderStatus): string {
  if (status === 'Livrée') return 'var(--brand-green)';
  if (status === 'Confirmée') return 'var(--secondary-500)';
  return 'var(--accent-500)';
}

export default function VendeurEspacePage() {
  const { user } = useAuth();
  const { tenant } = useTenant();

  const [tab, setTab] = useState<Tab>('overview');
  const [relation, setRelation] = useState<VendorRelation | null>(null);
  const [relationLoading, setRelationLoading] = useState(true);
  const [produits, setProduits] = useState<AnnonceItem[]>([]);
  const [commandes, setCommandes] = useState<CommandeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AnnonceItem | null>(null);
  const [formType, setFormType] = useState<string>('produits');
  const [deleting, setDeleting] = useState<AnnonceItem | null>(null);
  const [copied, setCopied] = useState(false);
  // Nombre de réservations reçues (badge du menu « Réservations »).
  const [reservationCount, setReservationCount] = useState(0);
  // Réservations chiffrées (montants) pour les statistiques de la vue d'ensemble.
  const [reservationStats, setReservationStats] = useState<VendorReservationStats>({ count: 0, amount: 0, rows: [] });

  const storeName = resolveStoreName(tenant, null);
  // Domaine personnalisé de la marketplace (si configuré & actif) : le lien
  // « Ma boutique publique » pointe alors vers ce domaine au lieu du sous-domaine.
  const [shopHost, setShopHost] = useState<string | null>(null);
  useEffect(() => {
    if (!tenant?.id) { setShopHost(null); return; }
    let active = true;
    fetchActiveCustomDomain(Number(tenant.id))
      .then((h) => { if (active) setShopHost(h); })
      .catch(() => { if (active) setShopHost(null); });
    return () => { active = false; };
  }, [tenant?.id]);
  const shopBase = String(__BASE_PATH__ || '/').replace(/\/$/, '');
  const shopOrigin =
    shopHost
      ? `https://${shopHost}`
      : (typeof window !== 'undefined' ? window.location.origin : '');
  const shopUrl = user ? `${shopOrigin}${shopBase}/boutique/${user.id}` : '#';
  // Reconnaissance vendeur : un compte n'est vendeur QUE sur la marketplace
  // où il a créé son compte vendeur (rattachement `userwebsite`). Le typecompte
  // vendeur (9) est global, mais le tableau de bord vendeur n'est accessible
  // que sur ce site-là.
  const isVendorOnThisSite = !!relation;
  const isGlobalVendor = !!user && user.typecompte === TYPE_COMPTE_VENDEUR;
  const currency = tenant?.monaie || 'MAD';

  const copyShopLink = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText(shopUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [prodRes, cmdRes] = await Promise.all([
        supabase.from('annonce').select('*').eq('idvendeur', user.id).order('created', { ascending: false }),
        supabase
          .from('commande')
          .select('id, titre, nomclient, quantite, totalttc, statut_com_vendeur, date_time, ville')
          .eq('idvendeur', user.id)
          .order('date_time', { ascending: false }),
      ]);
      if (prodRes.error) throw prodRes.error;
      setProduits((prodRes.data as AnnonceItem[]) || []);
      setCommandes((cmdRes.data as CommandeItem[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    document.title = 'Mon espace vendeur';
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Badge « Réservations » : compte les réservations prises sur les services du vendeur.
  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    fetchVendorReservationCount(user.id)
      .then((n) => { if (active) setReservationCount(n); })
      .catch(() => {});
    fetchVendorReservationStats(user.id)
      .then((s) => { if (active) setReservationStats(s); })
      .catch(() => {});
    return () => { active = false; };
  }, [user?.id, tab]);

  useEffect(() => {
    if (!user || !tenant?.id) {
      setRelationLoading(false);
      return;
    }
    let active = true;
    setRelationLoading(true);
    fetchVendorRelation(Number(tenant.id), user.id)
      .then((rel) => { if (active) setRelation(rel); })
      .catch(() => { if (active) setRelation(null); })
      .finally(() => { if (active) setRelationLoading(false); });
    return () => { active = false; };
  }, [user, tenant?.id]);

  const produitsList = useMemo(() => produits.filter((p) => isProductType(p.type)), [produits]);
  const servicesList = useMemo(() => produits.filter((p) => isServiceType(p.type)), [produits]);
  const annoncesList = useMemo(() => produits.filter((p) => isClassifiedType(p.type)), [produits]);
  const publishedCount = produits.filter((p) => p.publique === 1).length;

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    setProduits((p) => p.filter((x) => x.id !== target.id));
    try {
      await supabase.from('annonce').delete().eq('id', target.id);
    } catch {
      /* silent */
    }
  };

  const togglePublish = async (item: AnnonceItem) => {
    const next = item.publique === 1 ? 0 : 1;
    setProduits((p) => p.map((x) => (x.id === item.id ? { ...x, publique: next } : x)));
    try {
      await supabase.from('annonce').update({ publique: next }).eq('id', item.id);
    } catch {
      setProduits((p) => p.map((x) => (x.id === item.id ? { ...x, publique: item.publique } : x)));
    }
  };

  // Marque un contenu en rupture de stock / non disponible (ou de nouveau dispo).
  const toggleDisponible = async (item: AnnonceItem) => {
    const next = item.disponible === false; // indisponible → de nouveau dispo, sinon → indisponible
    const previous = produits;
    setProduits((p) => p.map((x) => (x.id === item.id ? { ...x, disponible: next } : x)));
    try {
      await setAnnonceDisponible(item.id, next);
    } catch {
      setProduits(previous);
      setError('Impossible de mettre à jour la disponibilité.');
    }
  };

  const updateOrderStatus = async (id: number, status: OrderStatus) => {
    const previous = commandes;
    setCommandes((prev) => prev.map((c) => (c.id === id ? { ...c, statut_com_vendeur: status } : c)));
    try {
      const { error: upErr } = await supabase
        .from('commande')
        .update({ statut_com_vendeur: status })
        .eq('id', id);
      if (upErr) throw upErr;
    } catch {
      setCommandes(previous);
      setError('Impossible de mettre à jour le statut de la commande.');
    }
  };

  // ── Not logged in ──
  if (!user) {
    return (
      <GuardBlock
        icon="ri-lock-line"
        title="Connexion requise"
        text="Connectez-vous à votre compte vendeur pour accéder à votre tableau de bord."
        cta={{ to: '/vendeur/inscription', label: 'Créer un compte vendeur', icon: 'ri-store-2-line' }}
      />
    );
  }

  if (relationLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  // Pas vendeur sur CE site : soit simple client, soit vendeur d'une autre
  // marketplace (son espace vendeur reste sur le site où il s'est inscrit).
  if (!isVendorOnThisSite) {
    return isGlobalVendor ? (
      <GuardBlock
        icon="ri-store-2-line"
        title="Espace vendeur"
        text={`Votre espace vendeur est rattaché à la marketplace où vous avez créé votre compte. Vous n'êtes pas vendeur sur ${storeName}.`}
        cta={{ to: '/', label: 'Retour à la boutique', icon: 'ri-arrow-left-line' }}
      />
    ) : (
      <GuardBlock
        icon="ri-store-2-line"
        title="Espace vendeur"
        text={`Vous êtes connecté(e) en tant que client. Pour vendre sur ${storeName}, créez un compte vendeur.`}
        cta={{ to: '/vendeur/inscription', label: 'Devenir vendeur', icon: 'ri-store-2-line' }}
      />
    );
  }

  const openNew = (type: string) => {
    setEditing(null);
    setFormType(type);
    setShowForm(true);
  };

  const openEdit = (p: AnnonceItem) => {
    setEditing(p);
    setFormType(p.type || 'produits');
    setShowForm(true);
  };

  const renderGrid = (list: AnnonceItem[]) => {
    if (list.length === 0) {
      return (
        <EmptyState
          icon="ri-shopping-bag-line"
          title="Aucun contenu pour le moment"
          text="Publiez votre premier contenu pour commencer à vendre sur la marketplace."
        />
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {list.map((p) => (
          <div key={p.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden group flex flex-col">
            <div className="aspect-[4/3] bg-background-100 relative">
              {p.image ? (
                <img src={p.image} alt={p.titre} className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-image-line text-3xl text-foreground-300"></i>
                </div>
              )}
              <span
                className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  p.publique === 1 ? 'bg-accent-500 text-background-50' : 'bg-foreground-900/70 text-background-50'
                }`}
              >
                {p.publique === 1 ? 'Publié' : 'Brouillon'}
              </span>
              {p.is_vendor_featured && (
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-500 text-background-50 flex items-center gap-1">
                  <i className="ri-star-fill"></i>
                  À la une
                </span>
              )}
              {p.disponible === false && (
                <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500 text-white flex items-center gap-1">
                  <i className="ri-forbid-2-line"></i>
                  Rupture / indisponible
                </span>
              )}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <h3 className="text-sm font-semibold text-foreground-900 truncate mb-1">{p.titre}</h3>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-primary-600">
                  {p.prix ? `${Number(p.prix).toLocaleString()} ${currency}` : '—'}
                </span>
                {p.ville && (
                  <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {p.ville}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-auto">
                <button
                  onClick={() => openEdit(p)}
                  className="flex-1 h-8 rounded-md bg-background-100 hover:bg-background-200/70 text-[11px] font-medium text-foreground-700 transition-colors cursor-pointer flex items-center justify-center gap-1"
                >
                  <i className="ri-edit-line"></i>Modifier
                </button>
                <button
                  onClick={() => toggleFeatured(p)}
                  title={p.is_vendor_featured ? 'Retirer de la une' : 'Mettre à la une'}
                  className={`w-8 h-8 rounded-md transition-colors cursor-pointer flex items-center justify-center ${
                    p.is_vendor_featured
                      ? 'bg-accent-100 text-accent-600 hover:bg-accent-200'
                      : 'bg-background-100 hover:bg-background-200/70 text-foreground-600'
                  }`}
                >
                  <i className={p.is_vendor_featured ? 'ri-star-fill' : 'ri-star-line'}></i>
                </button>
                <button
                  onClick={() => togglePublish(p)}
                  title={p.publique === 1 ? 'Masquer' : 'Publier'}
                  className="w-8 h-8 rounded-md bg-background-100 hover:bg-background-200/70 text-foreground-600 transition-colors cursor-pointer flex items-center justify-center"
                >
                  <i className={p.publique === 1 ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
                <button
                  onClick={() => toggleDisponible(p)}
                  title={p.disponible === false ? 'Marquer comme disponible' : 'Marquer en rupture / non disponible'}
                  className={`w-8 h-8 rounded-md transition-colors cursor-pointer flex items-center justify-center ${
                    p.disponible === false
                      ? 'bg-red-50 text-red-500 hover:bg-red-100'
                      : 'bg-background-100 hover:bg-background-200/70 text-foreground-600'
                  }`}
                >
                  <i className={p.disponible === false ? 'ri-forbid-2-line' : 'ri-checkbox-circle-line'}></i>
                </button>
                <button
                  onClick={() => setDeleting(p)}
                  title="Supprimer"
                  className="w-8 h-8 rounded-md bg-background-100 hover:bg-red-50 text-foreground-500 hover:text-red-500 transition-colors cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const allPublished = produits.filter((p) => p.publique === 1);

  // Met une annonce du vendeur « à la une » (une seule à la fois, sa boutique).
  const toggleFeatured = async (item: AnnonceItem) => {
    if (!user) return;
    const makeFeatured = !item.is_vendor_featured;
    const previous = produits;
    setProduits((prev) =>
      prev.map((x) => ({
        ...x,
        is_vendor_featured: x.id === item.id ? makeFeatured : false,
      })),
    );
    try {
      if (makeFeatured) await setVendorFeaturedAnnonce(user.id, item.id);
      else await clearVendorFeaturedAnnonce(user.id);
    } catch {
      setProduits(previous);
      setError('Impossible de mettre à jour la mise à la une.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* En-tête */}
      <section className="border-b border-background-200/70 bg-background-100/40">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-store-2-line text-xl text-background-50"></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                  {user.nomcommerce || user.name}
                </h1>
                <p className="text-sm text-foreground-500">Tableau de bord vendeur · {storeName}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Ma boutique publique
              </a>
              <button
                onClick={copyShopLink}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={copied ? 'ri-check-line' : 'ri-link'}></i>
                {copied ? 'Lien copié !' : 'Copier le lien'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-8">
        {relation && relation.validation !== 'approuve' && (
          <div className={`mb-5 px-4 py-3 rounded-lg border text-xs flex items-start gap-2 ${relation.validation === 'refuse' ? 'bg-red-50 border-red-100 text-red-700' : 'bg-accent-100/70 border-accent-200 text-accent-900'}`}>
            <i className={relation.validation === 'refuse' ? 'ri-alert-line mt-0.5' : 'ri-time-line mt-0.5'}></i>
            <span>
              {relation.validation === 'refuse'
                ? 'Votre demande de vente n\u2019a pas été acceptée pour le moment. Un avertissement vous a été adressé par la marketplace.'
                : 'Votre demande de vente est en attente de validation. Vos contenus ne sont pas encore visibles publiquement.'}
            </span>
          </div>
        )}

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Barre latérale */}
          <aside className="lg:w-60 flex-shrink-0">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {NAV.map((item) => (
                <button
                  key={item.key}
                  onClick={() => setTab(item.key)}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    tab === item.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${item.icon} text-base`}></i>
                  {item.label}
                  {item.key === 'reservations' && reservationCount > 0 && (
                    <span
                      className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                        tab === item.key ? 'bg-background-50 text-primary-600' : 'bg-primary-500 text-background-50'
                      }`}
                    >
                      {reservationCount > 99 ? '99+' : reservationCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          {/* Contenu */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
              </div>
            ) : (
              <ContentSwitch
                tab={tab}
                user={user}
                relation={relation}
                currency={currency}
                produits={produitsList}
                services={servicesList}
                annonces={annoncesList}
                commandes={commandes}
                reservationStats={reservationStats}
                publishedCount={publishedCount}
                annonceIds={allPublished.map((p) => p.id)}
                marketplaceId={tenant?.id}
                onNew={openNew}
                onGoto={setTab}
                onRelation={setRelation}
                renderGrid={renderGrid}
                onOrderStatus={updateOrderStatus}
              />
            )}
          </div>
        </div>
      </div>

      {showForm && user && (
        <VendorProductForm
          user={user}
          marketplaceId={tenant?.id}
          editing={editing}
          defaultType={formType}
          vendorCategoryId={relation?.categorieId ?? null}
          vendorCategoryNom={relation?.categorieNom ?? null}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); loadData(); }}
        />
      )}

      {deleting && (
        <DeleteConfirm
          title={deleting.titre}
          onCancel={() => setDeleting(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

// ── Sous-composants d'affichage ────────────────────────────────

function ContentSwitch({
  tab,
  user,
  relation,
  currency,
  produits,
  services,
  annonces,
  commandes,
  reservationStats,
  publishedCount,
  annonceIds,
  marketplaceId,
  onNew,
  onGoto,
  onRelation,
  renderGrid,
  onOrderStatus,
}: {
  tab: Tab;
  user: ZifekUser;
  relation: VendorRelation | null;
  currency: string;
  produits: AnnonceItem[];
  services: AnnonceItem[];
  annonces: AnnonceItem[];
  commandes: CommandeItem[];
  reservationStats: VendorReservationStats;
  publishedCount: number;
  annonceIds: number[];
  marketplaceId?: number;
  onNew: (type: string) => void;
  onGoto: (tab: Tab) => void;
  onRelation: (r: VendorRelation) => void;
  renderGrid: (list: AnnonceItem[]) => ReactNode;
  onOrderStatus: (id: number, status: OrderStatus) => void;
}) {
  switch (tab) {
    case 'overview':
      return (
        <VendorOverview
          counts={{
            produits: produits.length,
            services: services.length,
            annonces: annonces.length,
            publies: publishedCount,
          }}
          vendorId={user.id}
          commandes={commandes}
          reservations={reservationStats}
          currency={currency}
          pendingCount={commandes.filter((c) => orderStatusOf(c) === 'En attente').length}
          onGoto={onGoto as (t: 'produits' | 'services' | 'annonces' | 'reservations' | 'categorie') => void}
          onNewAnnonce={onNew}
        />
      );

    case 'produits':
      return (
        <SectionBlock icon="ri-shopping-bag-3-line" title="Mes produits" onNew={() => onNew('produits')} newLabel="Nouveau produit">
          {renderGrid(produits)}
        </SectionBlock>
      );

    case 'services':
      return (
        <SectionBlock icon="ri-service-line" title="Mes services" onNew={() => onNew('services')} newLabel="Nouveau service">
          {renderGrid(services)}
        </SectionBlock>
      );

    case 'annonces':
      return (
        <SectionBlock icon="ri-megaphone-line" title="Mes annonces" onNew={() => onNew('annonces')} newLabel="Nouvelle annonce">
          {renderGrid(annonces)}
        </SectionBlock>
      );

    case 'commandes':
      return <OrdersTable commandes={commandes} currency={currency} onStatus={onOrderStatus} />;

    case 'reservations':
      return <VendorReservations vendorId={user.id} />;

    case 'statistiques':
      return <VendorStatsTab vendorId={user.id} annonceIds={annonceIds} currency={currency} />;

    case 'bannieres':
      return <VendorBannersTab marketplaceId={marketplaceId} vendorId={user.id} annonces={[...produits, ...services, ...annonces]} />;

    case 'categorie':
      return (
        <VendorCategoryCard
          marketplaceId={marketplaceId}
          userId={user.id}
          relation={relation}
          onChanged={onRelation}
        />
      );

    case 'applications':
      return <VendorAppsTab marketplaceId={marketplaceId} vendorId={user.id} />;

    case 'parametres':
      return (
        <VendorSettings
          user={user}
          marketplaceId={marketplaceId}
          annonces={[...produits, ...services, ...annonces]}
        />
      );

    default:
      return null;
  }
}

function SectionBlock({
  icon,
  title,
  onNew,
  newLabel,
  children,
}: {
  icon: string;
  title: string;
  onNew: () => void;
  newLabel: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className={`${icon} text-primary-500`}></i>
          {title}
        </h2>
        <button
          onClick={onNew}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          {newLabel}
        </button>
      </div>
      {children}
    </div>
  );
}

function OrdersTable({
  commandes,
  currency,
  onStatus,
}: {
  commandes: CommandeItem[];
  currency: string;
  onStatus: (id: number, status: OrderStatus) => void;
}) {
  const [filter, setFilter] = useState<'all' | OrderStatus>('all');
  const filtered = commandes.filter((c) => filter === 'all' || orderStatusOf(c) === filter);
  const counts = ORDER_STATUSES.reduce<Record<string, number>>((acc, s) => {
    acc[s] = commandes.filter((c) => orderStatusOf(c) === s).length;
    return acc;
  }, {});

  return (
    <div>
      <h2 className="text-lg font-bold font-heading text-foreground-950 mb-5 flex items-center gap-2">
        <i className="ri-shopping-cart-2-line text-primary-500"></i>
        Commandes reçues
      </h2>

      {commandes.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-5">
          <button
            onClick={() => setFilter('all')}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
              filter === 'all' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
            }`}
          >
            Toutes ({commandes.length})
          </button>
          {ORDER_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                filter === s ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor(s) }} />
              {s} ({counts[s] || 0})
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon="ri-shopping-cart-line"
          title="Aucune commande"
          text={filter === 'all' ? 'Les commandes portant sur vos contenus apparaîtront ici.' : `Aucune commande avec le statut « ${filter} ».`}
        />
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-background-100/60 text-left">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Produit</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Client</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Qté</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Total</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Statut</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const status = orderStatusOf(c);
                  return (
                    <tr key={c.id} className="border-t border-background-200/70">
                      <td className="px-4 py-3 text-sm text-foreground-900">{c.titre || '—'}</td>
                      <td className="px-4 py-3 text-sm text-foreground-600">{c.nomclient || '—'}</td>
                      <td className="px-4 py-3 text-sm text-foreground-600">{c.quantite ?? 1}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground-900">
                        {c.totalttc ? `${Number(c.totalttc).toLocaleString()} ${currency}` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative inline-flex items-center">
                          <span
                            className="absolute left-2.5 w-2 h-2 rounded-full pointer-events-none"
                            style={{ backgroundColor: statusColor(status) }}
                          />
                          <select
                            value={status}
                            onChange={(e) => onStatus(c.id, e.target.value as OrderStatus)}
                            className="pl-6 pr-6 py-1.5 rounded-full text-[11px] font-medium bg-background-100 text-foreground-800 border border-background-200/70 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
                          >
                            {ORDER_STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-1.5 text-foreground-400 pointer-events-none text-sm"></i>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-500">
                        {c.date_time ? new Date(c.date_time).toLocaleDateString('fr-FR') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function GuardBlock({
  icon,
  title,
  text,
  cta,
}: {
  icon: string;
  title: string;
  text: string;
  cta: { to: string; label: string; icon: string };
}) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className={`${icon} text-2xl text-foreground-400`}></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">{title}</h1>
        <p className="text-sm text-foreground-500 mb-6">{text}</p>
        <Link
          to={cta.to}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className={cta.icon}></i>
          {cta.label}
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
        <i className={`${icon} text-2xl text-foreground-400`}></i>
      </div>
      <h3 className="text-base font-semibold text-foreground-900 mb-1">{title}</h3>
      <p className="text-sm text-foreground-500 max-w-xs">{text}</p>
    </div>
  );
}

function DeleteConfirm({ title, onCancel, onConfirm }: { title: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative w-full max-w-sm bg-background-50 border border-background-200/70 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-red-500 text-lg"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-950">Supprimer le contenu ?</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Cette action est irréversible.</p>
          </div>
        </div>
        <p className="text-xs text-foreground-600 mb-5">
          <strong>{title}</strong> sera définitivement supprimé.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-delete-bin-line"></i>
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}